<orbit-dialog-header [title]="data.title" />

<div class="dialog-content">
  @let isConfirmationValue = isConfirmation();
  @if (isConfirmationValue) {  <!-- Confirmation dialog content -->
    <div class="confirmation-message">
      <p>{{ data.message }}</p>
    </div>
  } @else () {
    <!-- Workflow form content -->
    <form [formGroup]="workflowForm" class="workflow-form">

      <!-- Conversation Type -->
      <div class="form-group">
        <label for="conversationTypeId">{{ 'SETTINGS.WORKFLOWS.CONVERSATION_TYPE' | translate }}</label>
        <select id="conversationTypeId" formControlName="conversationTypeId">
          <option value="">{{ 'SETTINGS.WORKFLOWS.ALL_CONVERSATION_TYPES' | translate }}</option>
          @let conversationTypesValue = conversationTypes();
          @for (type of conversationTypesValue; track type.id) {
            <option [value]="type.id">
              {{ type.id }}
            </option>
          }
        </select>
      </div>

      <!-- Trigger Section -->
      <div class="form-section" formGroupName="trigger">
        <div class="section-header">
          <h3>{{ 'SETTINGS.WORKFLOWS.TRIGGER' | translate }} *</h3>
        </div>

        <div class="section-content">
          <div class="form-group">
            <label for="triggerKind">{{ 'SETTINGS.WORKFLOWS.TRIGGER_KIND' | translate }} *</label>
            <select id="triggerKind" formControlName="triggerKind">
              @let triggerKindsValue = triggerKinds();
              @for (kind of triggerKindsValue; track kind) {
                <option [value]="kind">
                  {{ 'SETTINGS.WORKFLOWS.TRIGGER_KINDS.' + kind | translate }}
                </option>
              }
            </select>
            @if (workflowForm.get('trigger.triggerKind')?.invalid && workflowForm.get('trigger.triggerKind')?.touched) {
              <div class="error-message">
                {{ 'SETTINGS.WORKFLOWS.TRIGGER_KIND_REQUIRED' | translate }}
              </div>
            }
          </div>

          <!-- Message Sent Trigger specific fields -->
          @if (showField('trigger.messageSequence')) {
            <div class="form-group">
              <label for="messageSequence">{{ 'SETTINGS.WORKFLOWS.MESSAGE_SEQUENCE' | translate }} *</label>
              <input type="number" id="messageSequence" formControlName="messageSequence" min="0" />
              @if (workflowForm.get('trigger.messageSequence')?.invalid && workflowForm.get('trigger.messageSequence')?.touched) {
                <div class="error-message">
                  {{ 'SETTINGS.WORKFLOWS.MESSAGE_SEQUENCE_REQUIRED' | translate }}
                </div>
              }
              <span class="input-hint">{{ 'SETTINGS.WORKFLOWS.MESSAGE_SEQUENCE_HINT' | translate }}</span>
            </div>
          }

          @if (showField('trigger.participantKind')) {
            <div class="form-group">
              <label for="participantKind">{{ 'SETTINGS.WORKFLOWS.PARTICIPANT_KIND' | translate }}</label>
              <select id="participantKind" formControlName="participantKind">
                <option value="">{{ 'SETTINGS.WORKFLOWS.ANY_PARTICIPANT' | translate }}</option>
                <option value="CLIENT">{{ 'SETTINGS.WORKFLOWS.CLIENT' | translate }}</option>
                <option value="USER">{{ 'SETTINGS.WORKFLOWS.USER' | translate }}</option>
              </select>
              <span class="input-hint">{{ 'SETTINGS.WORKFLOWS.PARTICIPANT_KIND_HINT' | translate }}</span>
            </div>
          }

          <!-- Conversation Data Changed Trigger specific fields -->
          @if (workflowForm.get('trigger.triggerKind')?.value === 'CONVERSATION_DATA_CHANGED') {
            <wire-rule-editor
              [editorContent]="ruleExpression()"
              (contentChange)="ruleExpression.set($event)"
              (validChange)="ruleValid.set($event)"
              (errorChange)="updateRuleErrors($event)">
            </wire-rule-editor>

            @if (!ruleValid()) {
              <div class="error-message">
                {{ 'SETTINGS.WORKFLOWS.INVALID_RULE' | translate }}
                <ul>
                  @for (error of ruleEditorErrors(); track error) {
                    <li>{{ error }}</li>
                  }
                </ul>
              </div>
            }
          }

          @if (showField('trigger.operator')) {
            <div class="form-group">
              <label for="operator">{{ 'SETTINGS.WORKFLOWS.CONDITION_OPERATOR' | translate }} *</label>
              <select id="operator" formControlName="operator">
                @let conditionOperatorsValue = conditionOperators();
                @for (op of conditionOperatorsValue; track op) {
                  <option [value]="op">
                    {{ 'SETTINGS.WORKFLOWS.CONDITION_OPERATORS.' + op | translate }}
                  </option>
                }
              </select>
              @if (workflowForm.get('trigger.operator')?.invalid && workflowForm.get('trigger.operator')?.touched) {
                <div class="error-message">
                  {{ 'SETTINGS.WORKFLOWS.OPERATOR_REQUIRED' | translate }}
                </div>
              }
            </div>
          }

          @if (showField('trigger.oldValue') || showField('trigger.newValue')) {
            <div class="condition-fields">
              @if (showField('trigger.oldValue')) {
                <div class="form-group">
                  <label for="oldValue">{{ 'SETTINGS.WORKFLOWS.OLD_VALUE' | translate }} *</label>
                  <input type="text" id="oldValue" formControlName="oldValue" />
                  @if (workflowForm.get('trigger.oldValue')?.invalid && workflowForm.get('trigger.oldValue')?.touched) {
                    <div class="error-message">
                      {{ 'SETTINGS.WORKFLOWS.OLD_VALUE_REQUIRED' | translate }}
                    </div>
                  }
                </div>
              }

              @if (showField('trigger.newValue')) {
                <div class="form-group">
                  <label for="newValue">{{ 'SETTINGS.WORKFLOWS.NEW_VALUE' | translate }} *</label>
                  <input type="text" id="newValue" formControlName="newValue" />
                  @if (workflowForm.get('trigger.newValue')?.invalid && workflowForm.get('trigger.newValue')?.touched) {
                    <div class="error-message">
                      {{ 'SETTINGS.WORKFLOWS.NEW_VALUE_REQUIRED' | translate }}
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Action Section - Only shown when not using rule editor -->
      @if (workflowForm.get('trigger.triggerKind')?.value !== 'CONVERSATION_DATA_CHANGED') {
        <div class="form-section" formGroupName="action">
          <div class="section-header">
            <h3>{{ 'SETTINGS.WORKFLOWS.ACTION' | translate }} *</h3>
          </div>

          <div class="section-content">
            <div class="form-group">
              <label for="actionKind">{{ 'SETTINGS.WORKFLOWS.ACTION_KIND' | translate }} *</label>
              <select id="actionKind" formControlName="actionKind">
                @let actionKindsValue = actionKinds();
                @for (kind of actionKindsValue; track kind) {
                  <option [value]="kind">
                    {{ 'SETTINGS.WORKFLOWS.ACTION_KINDS.' + kind | translate }}
                  </option>
                }
              </select>
              @if (workflowForm.get('action.actionKind')?.invalid && workflowForm.get('action.actionKind')?.touched) {
                <div class="error-message">
                  {{ 'SETTINGS.WORKFLOWS.ACTION_KIND_REQUIRED' | translate }}
                </div>
              }
            </div>

            <!-- Send Message Action specific fields -->
            @if (showField('action.templateName')) {
              <div class="form-group">
                <label for="templateName">{{ 'SETTINGS.WORKFLOWS.MESSAGE_TEMPLATE' | translate }} *</label>
                <select id="templateName" formControlName="templateName">
                  @let messageTemplatesValue = messageTemplates();
                  @for (template of messageTemplatesValue; track template.name) {
                    <option [value]="template.name">
                      {{ template.name }}
                    </option>
                  }
                </select>
                @if (workflowForm.get('action.templateName')?.invalid && workflowForm.get('action.templateName')?.touched) {
                  <div class="error-message">
                    {{ 'SETTINGS.WORKFLOWS.MESSAGE_TEMPLATE_REQUIRED' | translate }}
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </form>
  }
</div>

<orbit-dialog-footer>
  <div class="right-addon" right-addon>
    <orbit-button
      [flat]="true"
      [label]="data.cancelLabel"
      (onClick)="closeDialog(false)" />
    <orbit-button
      variant="primary"
      [disabled]="!canSave()"
      [label]="data.confirmLabel"
      (onClick)="closeDialog(true)"
    />
  </div>
</orbit-dialog-footer>
